/**
 * Cookie Consent Store - Laumeris
 * Gestion du consentement cookies conforme CNIL/RGPD
 */

import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'

// Types de cookies
export interface CookiePreferences {
  necessary: boolean      // Toujours true, ne peut être désactivé
  analytics: boolean      // Cookies de mesure d'audience
  marketing: boolean      // Cookies publicitaires (non utilisés actuellement)
  preferences: boolean    // Cookies de préférences utilisateur
}

export interface ConsentRecord {
  version: string           // Version de la politique
  timestamp: string         // Date du consentement
  preferences: CookiePreferences
  userAgent: string         // Pour traçabilité
}

const CONSENT_KEY = 'laumeris_cookie_consent'
const CONSENT_VERSION = '1.0.0'
const CONSENT_DURATION_MONTHS = 13 // Durée max recommandée CNIL

export const useCookiesStore = defineStore('cookies', () => {
  // State
  const showBanner = ref(false)
  const showSettings = ref(false)
  const hasConsented = ref(false)
  const consentRecord = ref<ConsentRecord | null>(null)
  
  const preferences = ref<CookiePreferences>({
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false
  })

  // Computed
  const isAnalyticsEnabled = computed(() => preferences.value.analytics)
  const isMarketingEnabled = computed(() => preferences.value.marketing)
  const isPreferencesEnabled = computed(() => preferences.value.preferences)

  // Vérifier si le consentement est encore valide
  const isConsentValid = computed(() => {
    if (!consentRecord.value) return false
    
    const consentDate = new Date(consentRecord.value.timestamp)
    const expirationDate = new Date(consentDate)
    expirationDate.setMonth(expirationDate.getMonth() + CONSENT_DURATION_MONTHS)
    
    // Vérifier aussi la version
    if (consentRecord.value.version !== CONSENT_VERSION) return false
    
    return new Date() < expirationDate
  })

  // Initialiser le store
  function initialize() {
    const stored = localStorage.getItem(CONSENT_KEY)
    
    if (stored) {
      try {
        const record: ConsentRecord = JSON.parse(stored)
        consentRecord.value = record
        preferences.value = { ...record.preferences, necessary: true }
        hasConsented.value = true
        
        // Vérifier si le consentement est encore valide
        if (!isConsentValid.value) {
          // Consentement expiré ou version différente, redemander
          showBanner.value = true
          hasConsented.value = false
        }
      } catch {
        // Données corrompues, redemander le consentement
        localStorage.removeItem(CONSENT_KEY)
        showBanner.value = true
      }
    } else {
      // Première visite, afficher le bandeau
      showBanner.value = true
    }
  }

  // Accepter tous les cookies
  function acceptAll() {
    preferences.value = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    }
    saveConsent()
    closeBanner()
  }

  // Refuser tous les cookies (sauf nécessaires)
  function rejectAll() {
    preferences.value = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false
    }
    saveConsent()
    closeBanner()
  }

  // Sauvegarder les préférences personnalisées
  function savePreferences(newPreferences: Partial<CookiePreferences>) {
    preferences.value = {
      ...preferences.value,
      ...newPreferences,
      necessary: true // Toujours activé
    }
    saveConsent()
    closeSettings()
    closeBanner()
  }

  // Enregistrer le consentement
  function saveConsent() {
    const record: ConsentRecord = {
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
      preferences: { ...preferences.value },
      userAgent: navigator.userAgent
    }
    
    consentRecord.value = record
    hasConsented.value = true
    localStorage.setItem(CONSENT_KEY, JSON.stringify(record))
    
    // Appliquer les préférences
    applyPreferences()
  }

  // Appliquer les préférences de cookies
  function applyPreferences() {
    // Analytics
    if (preferences.value.analytics) {
      enableAnalytics()
    } else {
      disableAnalytics()
    }

    // Préférences utilisateur (thème, sidebar, etc.)
    if (!preferences.value.preferences) {
      // Ne pas supprimer les préférences existantes, juste ne plus en créer
      // L'utilisateur peut toujours avoir ses préférences de session
    }
  }

  // Activer les analytics (Cloudflare Web Analytics)
  function enableAnalytics() {
    // Cloudflare Web Analytics est privacy-first et ne nécessite pas
    // vraiment de consentement, mais on respecte le choix de l'utilisateur
    if (typeof window !== 'undefined') {
      (window as any).__cfAnalyticsEnabled = true
    }
  }

  // Désactiver les analytics
  function disableAnalytics() {
    if (typeof window !== 'undefined') {
      (window as any).__cfAnalyticsEnabled = false
    }
  }

  // Ouvrir les paramètres
  function openSettings() {
    showSettings.value = true
  }

  // Fermer les paramètres
  function closeSettings() {
    showSettings.value = false
  }

  // Fermer le bandeau
  function closeBanner() {
    showBanner.value = false
  }

  // Réouvrir le bandeau (pour modifier les préférences)
  function reopenBanner() {
    showBanner.value = true
    showSettings.value = true
  }

  // Révoquer le consentement
  function revokeConsent() {
    localStorage.removeItem(CONSENT_KEY)
    consentRecord.value = null
    hasConsented.value = false
    preferences.value = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false
    }
    showBanner.value = true
    applyPreferences()
  }

  // Exporter les données de consentement (RGPD)
  function exportConsentData(): string {
    return JSON.stringify(consentRecord.value, null, 2)
  }

  return {
    // State
    showBanner,
    showSettings,
    hasConsented,
    consentRecord,
    preferences,
    
    // Computed
    isAnalyticsEnabled,
    isMarketingEnabled,
    isPreferencesEnabled,
    isConsentValid,
    
    // Actions
    initialize,
    acceptAll,
    rejectAll,
    savePreferences,
    openSettings,
    closeSettings,
    closeBanner,
    reopenBanner,
    revokeConsent,
    exportConsentData
  }
})

